import {Feature} from '@hconnect/common/enums'
import type {Env} from '@hconnect/common/hproduce'
import {getSupportedLanguages} from '@hconnect/common/utils'
import {useLoginContext, useUserContext} from '@hconnect/login'
import {CommonHeader, useGuidedTours} from '@hconnect/uikit/src/lib2'
import type {User, NavItem} from '@hconnect/uikit/src/lib2'
import {useUpmPlantsContext, getPlantName} from '@hconnect/upm'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate} from 'react-router'

import {useAnyPermission, usePermission, usePermissionsQuery} from '../../permissions/hooks'
import {
  getUrl,
  MATERIALS_PERMISSION_TYPES,
  PLANT_ROUTES,
  PLANT_SETUP_PERMISSION_TYPES,
  ROUTES_PERMISSIONS
} from '../../routing'
import {useSettingsFeature} from '../hooks/useSettingsFeature'

interface SettingsHeaderProps {
  plantCode: string
}

export const SettingsHeader: React.FC<SettingsHeaderProps> = ({plantCode}) => {
  const {t, i18n} = useTranslation()

  const navigate = useNavigate()
  const location = useLocation()
  const pathName = location.pathname

  const upmPlants = useUpmPlantsContext()

  const {data: permissions} = usePermissionsQuery()
  const {logout} = useLoginContext()
  const {loggedUser} = useUserContext()

  const canViewPlantSetup = useAnyPermission(...PLANT_SETUP_PERMISSION_TYPES)

  const canViewStorages = usePermission('VIEW_MATERIAL_STORAGE')
  const canViewAssets = usePermission('VIEW_ASSETS')

  const canViewMaterials = useAnyPermission(...MATERIALS_PERMISSION_TYPES)
  const canViewMeasurements = usePermission(ROUTES_PERMISSIONS.MEASUREMENTS)
  const canViewSamplingPoints = usePermission(ROUTES_PERMISSIONS.SAMPLING_POINTS)

  // Feature flags
  // TODO: update the permission when new permission is created on BE
  // VIEW_MATERIAL_STORAGE and CHANGE_MATERIAL_STORAGE
  const isUpmStructureEnabled = useSettingsFeature(Feature.UpmAssetsAndStorages)

  const canViewPlantSetupTab = canViewPlantSetup
  const canViewStoragesTab = canViewStorages && !isUpmStructureEnabled
  const canViewAssetsTab = canViewAssets && !isUpmStructureEnabled

  const navItems = useMemo(() => {
    if (!plantCode) {
      return []
    }
    const navItemsConfig: {condition: boolean; item: NavItem}[] = [
      {
        condition: canViewPlantSetupTab,
        item: {
          label: t('upmSettings.plantSetup'),
          url: getUrl(PLANT_ROUTES.PLANT_SETUP.path, {plantCode}),
          dataTestId: 'navItem_plant_setup',
          id: 'plant_setup-nav-item'
        }
      },
      {
        condition: canViewStoragesTab,
        item: {
          label: t('storagesSettings.storages'),
          url: getUrl(PLANT_ROUTES.STORAGES.path, {plantCode}),
          dataTestId: 'navItem_storages',
          id: 'storages-nav-item'
        }
      },
      {
        condition: canViewAssetsTab,
        item: {
          label: t('assetsSettings.assets'),
          url: getUrl(PLANT_ROUTES.ASSETS.path, {plantCode}),
          dataTestId: 'navItem_assets'
        }
      },
      {
        condition: canViewMaterials,
        item: {
          label: t('materialsSettings.materials'),
          url: getUrl(PLANT_ROUTES.MATERIALS.path, {plantCode}),
          dataTestId: 'navItem_materials',
          id: 'materials-nav-item'
        }
      },
      {
        condition: canViewSamplingPoints,
        item: {
          label: t('samplingPointsSettings.samplingPoints'),
          url: getUrl(PLANT_ROUTES.SAMPLING_POINTS.path, {plantCode}),
          dataTestId: 'navItem_sampling_points',
          id: 'sampling-points-nav-item'
        }
      },
      {
        condition: canViewMeasurements,
        item: {
          label: t('measurementsSettings.measurements'),
          url: getUrl(PLANT_ROUTES.MEASUREMENTS.path, {plantCode}),
          dataTestId: 'navItem_measurements',
          id: 'measurements-nav-item'
        }
      }
    ]

    return navItemsConfig.filter(({condition}) => condition).map(({item}) => item)
  }, [
    plantCode,
    canViewPlantSetupTab,
    canViewStoragesTab,
    canViewAssetsTab,
    canViewMaterials,
    canViewSamplingPoints,
    canViewMeasurements,
    t
  ])

  const {tours, tourStatuses} = useGuidedTours()

  if (!plantCode) return null

  const user: User = {
    email: loggedUser?.eMail ?? undefined,
    name: loggedUser?.name ?? undefined
  }

  return (
    <CommonHeader
      appName="Settings"
      plant={{plantId: plantCode, plantName: getPlantName(upmPlants, plantCode)}}
      upmPlants={upmPlants}
      env={process.env.REACT_APP_STAGE as Env}
      permissions={permissions ?? []}
      navItems={navItems}
      navigate={navigate}
      locationPath={pathName}
      supportedLanguages={getSupportedLanguages(i18n)}
      user={user}
      onLogout={() => void logout()}
      guidedTours={{tours, statuses: tourStatuses}}
    />
  )
}
